/**
 * People IQ — Employee Create/Edit form.
 * - Employee code is a manual numeric input, IMMUTABLE after create.
 * - Runs live duplicate check on blur of PAN/Aadhaar/email/phone.
 * - Warns before submit if duplicates found → prompts for override reason.
 * - Statutory fields (PAN, Aadhaar) only visible when the viewer has
 *   `view_employee_sensitive` (backend-enforced masking).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/permissions";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Save, ArrowLeft, AlertTriangle, Lock, Loader2 } from "lucide-react";

const LIFECYCLE_OPTIONS = ["candidate", "active", "probation", "confirmed", "transferred", "suspended", "resigned", "terminated", "archived"];
const EMPLOYMENT_TYPES = ["full_time", "part_time", "contract", "intern", "consultant"];
// Sep 2026 — blood group is a fixed dropdown, never free text.
const BLOOD_GROUPS = [
  { v: "O+", l: "O positive (O+)" }, { v: "O-", l: "O negative (O-)" },
  { v: "A+", l: "A positive (A+)" }, { v: "A-", l: "A negative (A-)" },
  { v: "B+", l: "B positive (B+)" }, { v: "B-", l: "B negative (B-)" },
  { v: "AB+", l: "AB positive (AB+)" }, { v: "AB-", l: "AB negative (AB-)" },
];

export default function EmployeeForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    employee_code: "",
    full_name: "",
    preferred_name: "",
    personal_email: "",
    official_email: "",
    date_of_birth: "",
    gender: "",
    marital_status: "",
    personal_mobile: "",
    official_mobile: "",
    blood_group: "",
    // Employment
    date_of_joining: "",
    department_id: "",
    designation_id: "",
    location: "",
    cost_center: "",
    employment_type: "",
    grade_band: "",
    // Statutory
    pan: "",
    aadhaar: "",
    voter_id: "",
    driving_license: "",
    esic_number: "",
    uan_number: "",
    esic_applicable: false,
    pf_applicable: false,
    bank_account_number: "",
    bank_ifsc: "",
    bank_name: "",
    lifecycle_state: "active",
  });
  const [existing, setExisting] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [dupHits, setDupHits] = useState([]);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const canSeeSensitive = hasCapability(user, "view_employee_sensitive");
  const finalized = existing?.is_finalized;

  useEffect(() => {
    (async () => {
      const [{ data: depts }, { data: desigs }] = await Promise.all([
        api.get("/people/org/departments", { params: { active_only: true } }),
        api.get("/people/org/designations", { params: { active_only: true } }),
      ]);
      setDepartments(depts);
      setDesignations(desigs);
      // Load Asset IQ users for the self-service linkage picker. The People
      // module exposes its own minimal picker endpoint (id/name/email only),
      // gated on edit_employee — HR can link accounts without manage_users.
      try {
        const { data: usersList } = await api.get("/people/user-picker");
        setAppUsers(usersList);
      } catch { /* no edit_employee capability — skip */ }
      if (isEdit) {
        try {
          const { data } = await api.get(`/people/employees/${id}`);
          setExisting(data);
          setForm({
            ...form,
            ...data,
            // masked fields come as e.g. "******234F" — never overwrite them into form
            pan: canSeeSensitive ? (data.pan || "") : "",
            aadhaar: canSeeSensitive ? (data.aadhaar || "") : "",
            bank_account_number: canSeeSensitive ? (data.bank_account_number || "") : "",
          });
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Failed to load employee");
          navigate("/people");
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runDupCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/people/employees/check-duplicates", {
        employee_code: form.employee_code || null,
        personal_email: form.personal_email || null,
        official_email: form.official_email || null,
        personal_mobile: form.personal_mobile || null,
        pan: form.pan || null,
        aadhaar: form.aadhaar || null,
        date_of_birth: form.date_of_birth || null,
        full_name: form.full_name || null,
        exclude_id: isEdit ? id : null,
      });
      setDupHits(data.matches || []);
    } catch { /* silent */ } finally { setChecking(false); }
  };

  const submit = async (withOverride) => {
    if (!isEdit) {
      const isCandidate = form.lifecycle_state === "candidate";
      if (!isCandidate && !form.employee_code) return toast.error("Employee code is required (or set lifecycle to 'candidate')");
      if (form.employee_code && !/^\d+$/.test(form.employee_code)) return toast.error("Employee code must be digits only");
      if (!form.full_name) return toast.error("Full name is required");
      if (isCandidate && !form.personal_email && !form.personal_mobile) return toast.error("Candidate must have personal email or mobile");
    }
    setSaving(true);
    try {
      // Build payload — strip empty strings so we never overwrite existing values with ""
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) payload[k] = v;
      });
      // linked_user_id must be sent even when emptied so users can be UNLINKED.
      payload.linked_user_id = form.linked_user_id || null;
      // Statutory numbers go as explicit null when the scheme is switched off —
      // this is how a stored number is discarded when HR flips eligibility off.
      if (!form.pf_applicable) payload.uan_number = null;
      if (!form.esic_applicable) payload.esic_number = null;
      if (!isEdit && withOverride) payload.override_duplicate_reason = overrideReason;

      if (isEdit) {
        // Drop code and any locked fields the backend would reject.
        delete payload.employee_code;
        if (finalized) {
          ["full_name", "date_of_birth", "pan", "aadhaar", "voter_id", "driving_license"].forEach((k) => delete payload[k]);
        }
        await api.patch(`/people/employees/${id}`, payload);
        toast.success("Employee updated");
      } else {
        const { data } = await api.post("/people/employees", payload);
        toast.success(`Employee ${data.employee_code} created`);
        navigate(`/people/${data.id}`);
        return;
      }
      navigate(`/people/${id}`);
    } catch (e) {
      const det = e.response?.data?.detail;
      // Duplicate-warning envelope from the backend.
      if (e.response?.status === 409 && det && typeof det === "object" && det.code === "duplicate_warning") {
        setDupHits(det.matches || []);
        setOverrideOpen(true);
      } else {
        toast.error(formatApiError(det) || "Failed to save");
      }
    } finally {
      setSaving(false);
    }
  };

  const showLocked = isEdit && finalized;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="employee-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">People IQ</div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-0.5">
            {isEdit ? `Edit — ${existing?.employee_code}` : "New Employee"}
            {finalized && <Badge className="ml-2 bg-blue-100 text-blue-700"><Lock className="w-3 h-3 mr-1" /> Locked</Badge>}
          </h1>
        </div>
      </div>

      {finalized && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
          This record is <b>finalized</b> — legal identity fields (name, DOB, PAN, Aadhaar) cannot be edited here. Use <i>Unlock</i> from the profile page (privileged, audited).
        </div>
      )}

      {dupHits.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="dup-warning-banner">
          <div className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Possible duplicate(s) detected</div>
          <ul className="text-xs mt-1 pl-5 list-disc space-y-0.5">
            {dupHits.map((d) => (
              <li key={d.id}>
                <b>{d.employee_code}</b> — {d.full_name} <span className="text-slate-500">({d.reasons.join(" · ")})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Personal */}
      <Card>
        <CardHeader><CardTitle className="text-base">Personal</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label={form.lifecycle_state === "candidate" ? "Employee code (optional for candidate)" : "Employee code (numeric, immutable) *"}>
            <Input
              value={form.employee_code}
              onChange={(e) => setField("employee_code", e.target.value.replace(/[^\d]/g, ""))}
              onBlur={runDupCheck}
              disabled={isEdit}
              placeholder={form.lifecycle_state === "candidate" ? "Assign when they physically join" : "e.g. 1001"}
              data-testid="emp-code"
              className={isEdit ? "font-mono bg-slate-50" : "font-mono"}
            />
          </Field>
          <Field label="Full legal name" required disabled={showLocked}>
            <Input value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} onBlur={runDupCheck} disabled={showLocked} data-testid="emp-full-name" />
          </Field>
          <Field label="Preferred name">
            <Input value={form.preferred_name} onChange={(e) => setField("preferred_name", e.target.value)} data-testid="emp-preferred-name" />
          </Field>
          <Field label="Personal email">
            <Input type="email" value={form.personal_email} onChange={(e) => setField("personal_email", e.target.value)} onBlur={runDupCheck} data-testid="emp-personal-email" />
          </Field>
          <Field label="Official email">
            <Input type="email" value={form.official_email} onChange={(e) => setField("official_email", e.target.value)} onBlur={runDupCheck} data-testid="emp-official-email" />
          </Field>
          <Field label="Personal mobile">
            <Input value={form.personal_mobile} onChange={(e) => setField("personal_mobile", e.target.value)} onBlur={runDupCheck} placeholder="+91 ..." data-testid="emp-personal-mobile" />
          </Field>
          <Field label="Official mobile">
            <Input value={form.official_mobile} onChange={(e) => setField("official_mobile", e.target.value)} data-testid="emp-official-mobile" />
          </Field>
          <Field label="Date of birth" disabled={showLocked}>
            <Input type="date" value={form.date_of_birth || ""} onChange={(e) => setField("date_of_birth", e.target.value)} onBlur={runDupCheck} disabled={showLocked} data-testid="emp-dob" />
          </Field>
          <Field label="Gender">
            <Select value={form.gender || ""} onValueChange={(v) => setField("gender", v)}>
              <SelectTrigger data-testid="emp-gender"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {["male", "female", "other", "prefer_not_to_say"].map((g) => <SelectItem key={g} value={g}>{g.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Marital status">
            <Select value={form.marital_status || ""} onValueChange={(v) => setField("marital_status", v)}>
              <SelectTrigger data-testid="emp-marital"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {["single", "married", "divorced", "widowed", "other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Blood group">
            <Select value={form.blood_group || ""} onValueChange={(v) => setField("blood_group", v)}>
              <SelectTrigger data-testid="emp-blood"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {BLOOD_GROUPS.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}
                {form.blood_group && !BLOOD_GROUPS.some((b) => b.v === form.blood_group) && (
                  <SelectItem value={form.blood_group}>{form.blood_group} (legacy value)</SelectItem>
                )}
              </SelectContent>
            </Select>
          </Field>
        </CardContent>
      </Card>

      {/* Employment */}
      <Card>
        <CardHeader><CardTitle className="text-base">Employment</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Date of joining">
            <Input type="date" value={form.date_of_joining || ""} onChange={(e) => setField("date_of_joining", e.target.value)} data-testid="emp-doj" />
          </Field>
          <Field label="Department">
            <Select value={form.department_id || ""} onValueChange={(v) => setField("department_id", v)}>
              <SelectTrigger data-testid="emp-dept"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Designation">
            <Select value={form.designation_id || ""} onValueChange={(v) => setField("designation_id", v)}>
              <SelectTrigger data-testid="emp-desig"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {designations.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Location"><Input value={form.location} onChange={(e) => setField("location", e.target.value)} data-testid="emp-location" /></Field>
          <Field label="Cost center"><Input value={form.cost_center} onChange={(e) => setField("cost_center", e.target.value)} data-testid="emp-cc" /></Field>
          <Field label="Grade / band"><Input value={form.grade_band} onChange={(e) => setField("grade_band", e.target.value)} data-testid="emp-grade" /></Field>
          <Field label="Employment type">
            <Select value={form.employment_type || ""} onValueChange={(v) => setField("employment_type", v)}>
              <SelectTrigger data-testid="emp-emp-type"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {EMPLOYMENT_TYPES.map((g) => <SelectItem key={g} value={g}>{g.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Lifecycle state">
            <Select value={form.lifecycle_state || "active"} onValueChange={(v) => setField("lifecycle_state", v)}>
              <SelectTrigger data-testid="emp-lifecycle"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LIFECYCLE_OPTIONS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {appUsers.length > 0 && (
            <Field label="Linked Asset IQ account (self-service)">
              <Select
                value={form.linked_user_id || "__none__"}
                onValueChange={(v) => setField("linked_user_id", v === "__none__" ? "" : v)}
              >
                <SelectTrigger data-testid="emp-linked-user"><SelectValue placeholder="— Not linked —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not linked —</SelectItem>
                  {appUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} — {u.email} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Links this employee to an Asset IQ login so they can use My Profile self-service.
              </p>
            </Field>
          )}
        </CardContent>
      </Card>

      {/* Statutory — visible only if viewer has sensitive-view capability */}
      {canSeeSensitive && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Statutory & Bank
              <Badge variant="outline" className="text-[10px] font-normal">Sensitive</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="PAN" disabled={showLocked}><Input value={form.pan} onChange={(e) => setField("pan", e.target.value.toUpperCase())} onBlur={runDupCheck} placeholder="ABCDE1234F" maxLength={10} disabled={showLocked} data-testid="emp-pan" /></Field>
            <Field label="Aadhaar" disabled={showLocked}><Input value={form.aadhaar} onChange={(e) => setField("aadhaar", e.target.value.replace(/[^\d]/g, ""))} onBlur={runDupCheck} maxLength={12} disabled={showLocked} data-testid="emp-aadhaar" /></Field>
            <Field label="Voter ID" disabled={showLocked}><Input value={form.voter_id} onChange={(e) => setField("voter_id", e.target.value.toUpperCase())} disabled={showLocked} data-testid="emp-voter" /></Field>
            <Field label="Driving license" disabled={showLocked}><Input value={form.driving_license} onChange={(e) => setField("driving_license", e.target.value)} disabled={showLocked} data-testid="emp-dl" /></Field>
            <div className="md:col-span-3 grid grid-cols-2 gap-4 border-y border-slate-200 py-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!form.esic_applicable}
                  onChange={(e) => setField("esic_applicable", e.target.checked)}
                  data-testid="emp-esic-applicable" />
                <span>ESIC applicable</span>
                <span className="text-[10px] text-slate-400">— required for ESIC number and family/ESIC docs</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!form.pf_applicable}
                  onChange={(e) => setField("pf_applicable", e.target.checked)}
                  data-testid="emp-pf-applicable" />
                <span>PF (EPFO) applicable</span>
                <span className="text-[10px] text-slate-400">— required for PF/UAN and nominees</span>
              </label>
            </div>
            <Field label="ESIC number" disabled={!form.esic_applicable}>
              <Input value={form.esic_applicable ? form.esic_number : ""} onChange={(e) => setField("esic_number", e.target.value)}
                placeholder={form.esic_applicable ? "" : "Not applicable"}
                disabled={!form.esic_applicable} data-testid="emp-esic" />
            </Field>
            <Field label="PF / UAN Number" disabled={!form.pf_applicable}>
              <Input value={form.pf_applicable ? form.uan_number : ""} onChange={(e) => setField("uan_number", e.target.value)}
                placeholder={form.pf_applicable ? "" : "Not applicable"}
                disabled={!form.pf_applicable} data-testid="emp-uan" />
            </Field>
            <div />
            <Field label="Bank account #"><Input value={form.bank_account_number} onChange={(e) => setField("bank_account_number", e.target.value)} data-testid="emp-bank-acc" /></Field>
            <Field label="Bank IFSC"><Input value={form.bank_ifsc} onChange={(e) => setField("bank_ifsc", e.target.value.toUpperCase())} data-testid="emp-bank-ifsc" /></Field>
            <Field label="Bank name"><Input value={form.bank_name} onChange={(e) => setField("bank_name", e.target.value)} data-testid="emp-bank-name" /></Field>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-end gap-2 sticky bottom-0 py-3 -mx-6 px-6 bg-white border-t border-slate-100">
        {checking && <span className="text-xs text-slate-500">Checking duplicates…</span>}
        <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          onClick={() => submit(false)}
          disabled={saving}
          className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
          data-testid="emp-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {isEdit ? "Save changes" : "Create Employee"}
        </Button>
      </div>

      {/* Duplicate-override dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent data-testid="dup-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Confirm Duplicate Override
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p className="text-slate-600">The record you're creating overlaps with these existing employees:</p>
            <ul className="text-xs pl-5 list-disc space-y-1 max-h-40 overflow-auto">
              {dupHits.map((d) => (
                <li key={d.id}><b>{d.employee_code}</b> — {d.full_name} ({d.reasons.join(" · ")})</li>
              ))}
            </ul>
            <Label className="text-xs">Reason to proceed anyway (audited)</Label>
            <Textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="e.g. Different person, same shared family phone."
              rows={3}
              data-testid="dup-override-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideOpen(false)}>Cancel</Button>
            <Button
              disabled={overrideReason.trim().length < 5 || saving}
              onClick={() => { setOverrideOpen(false); submit(true); }}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="dup-override-confirm"
            >
              Create anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, disabled, children }) {
  return (
    <div className={disabled ? "opacity-70" : ""}>
      <Label className="text-xs font-semibold uppercase text-slate-600 flex items-center gap-1">
        {label}
        {required && <span className="text-rose-500">*</span>}
        {disabled && <Lock className="w-3 h-3 text-blue-500" />}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
